/**
 * EmailNotificationService
 * Sends notification emails via the /api/send-notification-email serverless function.
 * All emails are branded with the practice's logo, name, and contact info.
 * Falls back gracefully if SMTP is not configured (logs instead of failing).
 */

import firebaseAuthService from './firebase/FirebaseAuthService';
import CompanySettingsService from './CompanySettingsService';

interface EmailPayload {
  to: string;
  subject: string;
  htmlBody: string;
  textBody: string;
}

interface PracticeBranding {
  name: string;
  logo?: string; // base64 data URI
  phone: string;
  email: string;
  address: string;
  website: string;
}

class EmailNotificationService {
  private static instance: EmailNotificationService;

  static getInstance(): EmailNotificationService {
    if (!EmailNotificationService.instance) {
      EmailNotificationService.instance = new EmailNotificationService();
    }
    return EmailNotificationService.instance;
  }

  /** Get practice branding from CompanySettingsService */
  private getBranding(): PracticeBranding {
    const settings = CompanySettingsService.getInstance().getSettings();
    const addr = settings.address;
    const addressStr = [addr.street, addr.city, addr.state, addr.zipCode].filter(Boolean).join(', ');

    return {
      name: settings.companyName || 'Your Practice',
      logo: settings.companyLogo || undefined,
      phone: settings.phone || '',
      email: settings.email || '',
      address: addressStr,
      website: settings.website || '',
    };
  }

  /** Build the HTML email header with practice logo + name */
  private buildHeader(branding: PracticeBranding): string {
    const logoHtml = branding.logo
      ? `<img src="${branding.logo}" alt="${branding.name}" style="max-height: 60px; max-width: 200px; margin-bottom: 8px;" /><br/>`
      : '';

    return `
      <div style="text-align: center; margin-bottom: 24px;">
        ${logoHtml}
        <h2 style="margin: 0; color: #6CA6C1; font-size: 22px; font-weight: 700;">${branding.name}</h2>
      </div>`;
  }

  /** Build the HTML email footer with practice contact info */
  private buildFooter(branding: PracticeBranding, providerName?: string): string {
    const contactLines: string[] = [];
    if (branding.phone) contactLines.push(branding.phone);
    if (branding.email) contactLines.push(branding.email);
    if (branding.address) contactLines.push(branding.address);
    if (branding.website) contactLines.push(`<a href="${branding.website.startsWith('http') ? branding.website : 'https://' + branding.website}" style="color: #6CA6C1; text-decoration: none;">${branding.website}</a>`);

    return `
      <hr style="border: none; border-top: 1px solid #334155; margin: 24px 0;" />
      ${providerName ? `
      <p style="margin: 0 0 12px 0; font-size: 13px; color: #64748b;">
        Best regards,<br/>
        <strong style="color: #94a3b8;">${providerName}</strong><br/>
        ${branding.name}
      </p>` : `
      <p style="margin: 0 0 12px 0; font-size: 13px; color: #64748b;">
        — The ${branding.name} Team
      </p>`}
      ${contactLines.length > 0 ? `
      <div style="font-size: 12px; color: #475569; line-height: 1.6;">
        ${contactLines.join(' &nbsp;|&nbsp; ')}
      </div>` : ''}`;
  }

  /** Wrap content in the branded email shell */
  private wrapEmail(content: string, branding: PracticeBranding, providerName?: string): string {
    return `
      <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 600px; margin: 0 auto; padding: 24px;">
        <div style="background-color: #0f172a; border-radius: 12px; padding: 32px; color: #f8fafc;">
          ${this.buildHeader(branding)}
          ${content}
          ${this.buildFooter(branding, providerName)}
        </div>
        <p style="margin: 16px 0 0 0; font-size: 11px; color: #888; text-align: center;">
          This is an automated message from ${branding.name}. Please do not reply to this email.
        </p>
      </div>`;
  }

  /** Build a CTA button */
  private buildButton(text: string, url: string): string {
    return `
      <div style="margin: 24px 0; text-align: center;">
        <a href="${url}"
           style="display: inline-block; padding: 14px 28px; background-color: #6CA6C1; color: #0f172a;
                  text-decoration: none; border-radius: 8px; font-weight: 600; font-size: 15px;">
          ${text}
        </a>
      </div>`;
  }

  private async sendEmail(payload: EmailPayload): Promise<boolean> {
    try {
      const token = await firebaseAuthService.getIdToken();
      if (!token) {
        console.warn('[Email] No auth token — cannot send email');
        return false;
      }

      const response = await fetch('/api/send-notification-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.sent) {
        return true;
      } else {
        console.warn('[Email] Not sent:', data.reason || data.error);
        return false;
      }
    } catch (err) {
      console.error('[Email] Failed to send notification:', err);
      return false;
    }
  }

  // ─── APPOINTMENT CONFIRMATION ──────────────────────────────────────────────

  async sendAppointmentConfirmation(params: {
    clientEmail: string;
    clientName: string;
    providerName: string;
    appointmentDate: string;
    startTime: string;
    modality: 'video' | 'in-person';
    sessionType: string;
  }): Promise<boolean> {
    const { clientEmail, clientName, providerName, appointmentDate, startTime, modality, sessionType } = params;
    if (!clientEmail) return false;

    const branding = this.getBranding();
    const firstName = clientName.split(' ')[0] || 'there';
    const siteUrl = window.location.origin;
    const modalityLabel = modality === 'video' ? 'Video Session' : 'In-Person Visit';
    const sessionLabel = sessionType.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

    const content = `
      <p style="margin: 0 0 16px 0; font-size: 16px;">Dear ${firstName},</p>
      <p style="margin: 0 0 16px 0; font-size: 15px; color: #94a3b8;">
        Your appointment has been confirmed! Here are your details:
      </p>
      <div style="background-color: #1e293b; border-radius: 8px; padding: 20px; margin: 0 0 20px 0;">
        <table style="width: 100%; border-collapse: collapse;">
          <tr><td style="padding: 8px 0; font-size: 14px; color: #64748b; width: 110px;">Date</td><td style="padding: 8px 0; font-size: 15px; color: #f8fafc; font-weight: 600;">${appointmentDate}</td></tr>
          <tr><td style="padding: 8px 0; font-size: 14px; color: #64748b;">Time</td><td style="padding: 8px 0; font-size: 15px; color: #f8fafc; font-weight: 600;">${startTime}</td></tr>
          <tr><td style="padding: 8px 0; font-size: 14px; color: #64748b;">Provider</td><td style="padding: 8px 0; font-size: 15px; color: #f8fafc; font-weight: 600;">${providerName}</td></tr>
          <tr><td style="padding: 8px 0; font-size: 14px; color: #64748b;">Type</td><td style="padding: 8px 0; font-size: 15px; color: #f8fafc; font-weight: 600;">${modalityLabel}</td></tr>
          <tr><td style="padding: 8px 0; font-size: 14px; color: #64748b;">Session</td><td style="padding: 8px 0; font-size: 15px; color: #f8fafc; font-weight: 600;">${sessionLabel}</td></tr>
        </table>
      </div>
      <p style="margin: 0 0 16px 0; font-size: 15px; color: #94a3b8;">
        ${modality === 'video'
          ? 'Your video session link will be available in your portal when it\'s time for your appointment.'
          : 'Please arrive 10 minutes early for your in-person visit.'}
      </p>
      ${this.buildButton('View Your Appointment', siteUrl)}
      <p style="margin: 0; font-size: 13px; color: #64748b; text-align: center;">
        Need to reschedule or cancel? Log in to your portal or contact your provider.
      </p>`;

    const subject = `Appointment Confirmed — ${branding.name}`;
    const htmlBody = this.wrapEmail(content, branding, providerName);

    const textBody = `Dear ${firstName},

Your appointment has been confirmed!

Date: ${appointmentDate}
Time: ${startTime}
Provider: ${providerName}
Type: ${modalityLabel}
Session: ${sessionLabel}

${modality === 'video' ? 'Your video session link will be available in your portal when it\'s time.' : 'Please arrive 10 minutes early for your in-person visit.'}

View your appointment at ${siteUrl}

Need to reschedule or cancel? Log in to your portal or contact your provider.

${providerName}
${branding.name}${branding.phone ? ' | ' + branding.phone : ''}`;

    return this.sendEmail({ to: clientEmail, subject, htmlBody, textBody });
  }

  // ─── CANCELLATION NOTIFICATION ─────────────────────────────────────────────

  async sendCancellationNotification(params: {
    clientEmail: string;
    clientName: string;
    providerName: string;
    appointmentDate: string;
    wantsToReschedule: boolean;
  }): Promise<boolean> {
    const { clientEmail, clientName, providerName, appointmentDate, wantsToReschedule } = params;
    if (!clientEmail) return false;

    const branding = this.getBranding();
    const firstName = clientName.split(' ')[0] || 'there';
    const siteUrl = window.location.origin;

    const rescheduleMessage = wantsToReschedule
      ? `We received your reschedule preference and ${providerName} will follow up shortly.`
      : 'When you\'re ready, please reschedule at your convenience:';

    const content = `
      <p style="margin: 0 0 16px 0; font-size: 16px;">Dear ${firstName},</p>
      <p style="margin: 0 0 16px 0; font-size: 15px; color: #94a3b8;">
        We hope all is well. Your appointment on <strong style="color: #f8fafc;">${appointmentDate}</strong>
        with <strong style="color: #f8fafc;">${providerName}</strong> has been cancelled as requested.
      </p>
      <p style="margin: 0 0 16px 0; font-size: 15px; color: #94a3b8;">${rescheduleMessage}</p>
      ${!wantsToReschedule ? this.buildButton('Schedule Your Next Appointment', siteUrl) + `
      <p style="margin: 0; font-size: 14px; color: #64748b; text-align: center;">
        Or call your provider directly to reschedule.
      </p>` : ''}
      <p style="margin: 16px 0 0 0; font-size: 13px; color: #64748b;">
        We look forward to seeing you at your next visit.
      </p>`;

    const subject = `Your Appointment Update — ${branding.name}`;
    const htmlBody = this.wrapEmail(content, branding, providerName);

    const textBody = `Dear ${firstName},

We hope all is well. Your appointment on ${appointmentDate} with ${providerName} has been cancelled as requested.

${rescheduleMessage}

${!wantsToReschedule ? `Visit ${siteUrl} or call your provider to schedule your next appointment.` : ''}

We look forward to seeing you at your next visit.

${providerName}
${branding.name}${branding.phone ? ' | ' + branding.phone : ''}`;

    return this.sendEmail({ to: clientEmail, subject, htmlBody, textBody });
  }

  // ─── NO-SHOW NOTIFICATION ─────────────────────────────────────────────────

  async sendNoShowNotification(params: {
    clientEmail: string;
    clientName: string;
    providerName: string;
    appointmentDate: string;
  }): Promise<boolean> {
    const { clientEmail, clientName, providerName, appointmentDate } = params;
    if (!clientEmail) return false;

    const branding = this.getBranding();
    const firstName = clientName.split(' ')[0] || 'there';
    const siteUrl = window.location.origin;

    const content = `
      <p style="margin: 0 0 16px 0; font-size: 16px;">Dear ${firstName},</p>
      <p style="margin: 0 0 16px 0; font-size: 15px; color: #94a3b8;">
        We noticed you were unable to make your appointment on
        <strong style="color: #f8fafc;">${appointmentDate}</strong>
        with <strong style="color: #f8fafc;">${providerName}</strong>.
      </p>
      <p style="margin: 0 0 16px 0; font-size: 15px; color: #94a3b8;">
        We understand things come up. Please reschedule at your earliest convenience so we can continue supporting your care.
      </p>
      ${this.buildButton('Reschedule Your Appointment', siteUrl)}
      <p style="margin: 0; font-size: 14px; color: #64748b; text-align: center;">
        Or call your provider directly to reschedule.
      </p>`;

    const subject = `Missed Appointment — ${branding.name}`;
    const htmlBody = this.wrapEmail(content, branding, providerName);

    const textBody = `Dear ${firstName},

We noticed you were unable to make your appointment on ${appointmentDate} with ${providerName}.

We understand things come up. Please reschedule at your earliest convenience.

Visit ${siteUrl} or call your provider to reschedule.

${providerName}
${branding.name}${branding.phone ? ' | ' + branding.phone : ''}`;

    return this.sendEmail({ to: clientEmail, subject, htmlBody, textBody });
  }

  // ─── BILLING / PAYMENT REMINDER ───────────────────────────────────────────

  async sendBillingReminder(params: {
    clientEmail: string;
    clientName: string;
    providerName: string;
    balanceAmount: string;     // e.g., "$150.00"
    balanceDetails?: string;   // e.g., "Session on Mar 5, 2026"
    dueDate?: string;          // e.g., "March 20, 2026"
  }): Promise<boolean> {
    const { clientEmail, clientName, providerName, balanceAmount, balanceDetails, dueDate } = params;
    if (!clientEmail) return false;

    const branding = this.getBranding();
    const firstName = clientName.split(' ')[0] || 'there';
    const siteUrl = window.location.origin;

    const content = `
      <p style="margin: 0 0 16px 0; font-size: 16px;">Dear ${firstName},</p>
      <p style="margin: 0 0 16px 0; font-size: 15px; color: #94a3b8;">
        This is a friendly reminder that you have an outstanding balance on your account.
      </p>
      <div style="background-color: #1e293b; border-radius: 8px; padding: 20px; margin: 0 0 20px 0; text-align: center;">
        <p style="margin: 0 0 4px 0; font-size: 14px; color: #64748b;">Amount Due</p>
        <p style="margin: 0; font-size: 32px; font-weight: 700; color: #F7E9A0;">${balanceAmount}</p>
        ${dueDate ? `<p style="margin: 8px 0 0 0; font-size: 13px; color: #94a3b8;">Due by: ${dueDate}</p>` : ''}
        ${balanceDetails ? `<p style="margin: 8px 0 0 0; font-size: 13px; color: #64748b;">${balanceDetails}</p>` : ''}
      </div>
      <p style="margin: 0 0 16px 0; font-size: 15px; color: #94a3b8;">
        You can make a payment securely through your patient portal. If you have questions about your bill or need to set up a payment plan, please contact our office.
      </p>
      ${this.buildButton('Make a Payment', siteUrl)}
      ${branding.phone ? `
      <p style="margin: 0; font-size: 14px; color: #64748b; text-align: center;">
        Questions? Call us at <strong style="color: #94a3b8;">${branding.phone}</strong>
      </p>` : ''}`;

    const subject = `Payment Reminder — ${branding.name}`;
    const htmlBody = this.wrapEmail(content, branding, providerName);

    const textBody = `Dear ${firstName},

This is a friendly reminder that you have an outstanding balance on your account.

Amount Due: ${balanceAmount}${dueDate ? '\nDue by: ' + dueDate : ''}${balanceDetails ? '\n' + balanceDetails : ''}

You can make a payment securely through your patient portal at ${siteUrl}

If you have questions about your bill or need to set up a payment plan, please contact our office${branding.phone ? ' at ' + branding.phone : ''}.

${providerName}
${branding.name}${branding.phone ? ' | ' + branding.phone : ''}`;

    return this.sendEmail({ to: clientEmail, subject, htmlBody, textBody });
  }

  // ─── APPOINTMENT CONFIRMATION SMS ─────────────────────────────────────────

  async sendAppointmentConfirmationSMS(params: {
    clientPhone: string;
    clientName: string;
    providerName: string;
    appointmentDate: string;
    startTime: string;
    modality: 'video' | 'in-person';
  }): Promise<boolean> {
    const { clientPhone, clientName, providerName, appointmentDate, startTime, modality } = params;
    if (!clientPhone) return false;

    const branding = this.getBranding();
    const firstName = clientName.split(' ')[0] || 'there';
    const modalityLabel = modality === 'video' ? 'Video' : 'In-person';
    const message = `Hi ${firstName}! Your ${modalityLabel} appointment with ${providerName} is confirmed for ${appointmentDate} at ${startTime}. Log in to your ${branding.name} portal for details.`;

    try {
      const token = await firebaseAuthService.getIdToken();
      if (!token) return false;

      const response = await fetch('/api/send-sms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ to: clientPhone, message }),
      });

      const data = await response.json();
      return data.sent || false;
    } catch (err) {
      console.error('[SMS] Confirmation failed:', err);
      return false;
    }
  }

  // ─── TEAM MEMBER INVITE ───────────────────────────────────────────────────

  async sendTeamInvite(params: {
    recipientEmail: string;
    recipientName: string;
    role: string;
    invitedBy: string;
    practiceName?: string;
  }): Promise<boolean> {
    const { recipientEmail, recipientName, role, invitedBy } = params;
    if (!recipientEmail) return false;

    const branding = this.getBranding();
    const practiceName = params.practiceName || branding.name;
    const firstName = recipientName.split(' ')[0] || 'there';
    const siteUrl = window.location.origin;
    const signupUrl = `${siteUrl}/provider-signup?invite=true&email=${encodeURIComponent(recipientEmail)}&role=${encodeURIComponent(role)}`;

    const content = `
      <p style="margin: 0 0 16px 0; font-size: 16px;">Hi ${firstName},</p>
      <p style="margin: 0 0 16px 0; font-size: 15px; color: #94a3b8;">
        <strong style="color: #f8fafc;">${invitedBy}</strong> has invited you to join
        <strong style="color: #f8fafc;">${practiceName}</strong> as a
        <strong style="color: #F7E9A0;">${role}</strong>.
      </p>
      <p style="margin: 0 0 24px 0; font-size: 15px; color: #94a3b8;">
        Click the button below to create your account and complete your profile.
      </p>
      ${this.buildButton('Accept Invitation', signupUrl)}
      <p style="margin: 16px 0 0 0; font-size: 13px; color: #64748b; text-align: center;">
        Or copy this link: <a href="${signupUrl}" style="color: #6CA6C1; word-break: break-all;">${signupUrl}</a>
      </p>
      <p style="margin: 16px 0 0 0; font-size: 13px; color: #64748b;">
        If you weren't expecting this invitation, you can safely ignore this email.
      </p>`;

    const subject = `You're invited to join ${practiceName}`;
    const htmlBody = this.wrapEmail(content, branding);

    const textBody = `Hi ${firstName},

${invitedBy} has invited you to join ${practiceName} as a ${role}.

Create your account here: ${signupUrl}

If you weren't expecting this invitation, you can safely ignore this email.

— The ${practiceName} Team`;

    return this.sendEmail({ to: recipientEmail, subject, htmlBody, textBody });
  }

  // ─── CRISIS ALERT NOTIFICATION ──────────────────────────────────────────────

  async sendCrisisAlertEmail(params: {
    providerEmail: string;
    providerName: string;
    patientName: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
    keywords: string[];
    triggerMessage: string;
    timestamp: Date;
  }): Promise<boolean> {
    const { providerEmail, providerName, patientName, severity, keywords, triggerMessage, timestamp } = params;
    const branding = this.getBranding();
    const siteUrl = process.env.REACT_APP_SITE_URL || window.location.origin;
    const severityLabel = severity.charAt(0).toUpperCase() + severity.slice(1);
    const timeStr = timestamp.toLocaleString();

    const subject = `⚠️ Crisis Alert: ${patientName} — ${severityLabel} Severity`;

    const htmlBody = `
      ${this.buildHeader(branding)}
      <div style="background-color: ${severity === 'critical' ? '#FEE2E2' : '#FEF3C7'}; border: 2px solid ${severity === 'critical' ? '#EF4444' : '#F59E0B'}; border-radius: 8px; padding: 20px; margin-bottom: 20px;">
        <h2 style="margin: 0 0 8px 0; color: ${severity === 'critical' ? '#991B1B' : '#92400E'}; font-size: 18px;">
          Crisis Alert — ${severityLabel} Severity
        </h2>
        <p style="margin: 0; color: #374151; font-size: 14px;">
          A crisis indicator was detected for <strong>${patientName}</strong> at ${timeStr}.
        </p>
      </div>
      <div style="margin-bottom: 16px;">
        <p style="font-size: 14px; color: #374151; margin: 0 0 8px 0;"><strong>Keywords detected:</strong> ${keywords.join(', ')}</p>
        <p style="font-size: 14px; color: #374151; margin: 0 0 8px 0;"><strong>Message:</strong></p>
        <div style="background: #F3F4F6; border-radius: 6px; padding: 12px; font-size: 14px; color: #1F2937; font-style: italic;">
          "${triggerMessage}"
        </div>
      </div>
      <div style="margin-top: 20px;">
        ${this.buildButton(siteUrl, 'Review in Dashboard')}
      </div>
      <p style="font-size: 13px; color: #6B7280; margin-top: 20px;">
        Please follow your practice's crisis protocol. This alert has been logged in the system.
      </p>
      ${this.buildFooter(branding)}`;

    const textBody = `CRISIS ALERT — ${severityLabel} Severity

Patient: ${patientName}
Time: ${timeStr}
Keywords: ${keywords.join(', ')}

Message: "${triggerMessage}"

Please review immediately: ${siteUrl}
Follow your practice's crisis protocol. This alert has been logged.

${branding.name}${branding.phone ? ' | ' + branding.phone : ''}`;

    return this.sendEmail({ to: providerEmail, subject, htmlBody, textBody });
  }
}

export default EmailNotificationService;
